import {
  Input,
  Select,
  Tooltip,
  TooltipTrigger,
} from "@light-technology/components";
import { CurrencyDollar, Lock01, Pencil01 } from "@untitledui/icons";
import type { Key } from "react";
import { useController, type Control } from "react-hook-form";
import {
  type AppConfigurationResponse,
  type PlanDetailResponse,
} from "../../state/api.types";
import { PARTNER_SUCCESS_EMAIL } from "../../utilities/constants";
import type { FormValues, TermMonths } from "./common";
import { usePlanConstraintByFieldType } from "./common";
import { ViewOnlyField } from "./ViewOnlyField";

const TERM_MONTHS_ITEMS = [
  {
    id: "12",
    label: "12 Months",
  },
  {
    id: "24",
    label: "24 Months",
  },
  {
    id: "36",
    label: "36 Months",
  },
];

type EditDesignProps = {
  plan: PlanDetailResponse;
  configuration: AppConfigurationResponse;
  control: Control<FormValues>;
  setTermMonths: (value: TermMonths) => void;
  isEditingDisabled: boolean;
};

function formatDollarAmount(dollarAmount: number): string {
  if (dollarAmount < 0) {
    return `-$${(dollarAmount * -1).toString()}`;
  }
  if (!Number.isInteger(dollarAmount)) {
    return `$${dollarAmount.toFixed(2)}`;
  }
  return `$${dollarAmount.toString()}`;
}

export const EditDesign = ({
  isEditingDisabled,
  plan,
  configuration,
  control,
  setTermMonths,
}: EditDesignProps) => {
  const constraintByFieldType = usePlanConstraintByFieldType(plan);
  const baseFeeConstraint = constraintByFieldType.MONTHLY_COST;
  const monthlyCommissionConstraint =
    constraintByFieldType.PARTNER_MARGIN_PER_MONTH;
  const { field: termMonthsField } = useController({
    control,
    name: "termMonths",
    rules: { required: true },
  });
  const { onChange: onTermMonthsChange, ...termMonthsFieldProps } =
    termMonthsField;
  const { field: baseFeeField, fieldState: baseFeeFieldState } = useController({
    control,
    name: "baseFee",
    rules: baseFeeConstraint && {
      min: parseFloat(baseFeeConstraint.min_value),
      max: parseFloat(baseFeeConstraint.max_value),
      required: true,
    },
  });
  const {
    field: monthlyCommissionField,
    fieldState: monthlyCommissionFieldState,
  } = useController({
    control,
    name: "monthlyCommission",
    rules: monthlyCommissionConstraint && {
      min: parseFloat(monthlyCommissionConstraint.min_value),
      max: parseFloat(monthlyCommissionConstraint.max_value),
      required: true,
    },
  });
  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex w-full flex-row items-center gap-md">
        <Pencil01 />
        <div className="w-full text-lg font-semibold">Design</div>
        {isEditingDisabled && (
          <Tooltip
            arrow
            title={`Editing is not available for this plan. Please reach out to us at ${PARTNER_SUCCESS_EMAIL} for assistance.`}
          >
            <TooltipTrigger>
              <Lock01 />
            </TooltipTrigger>
          </Tooltip>
        )}
      </div>
      {isEditingDisabled ? (
        <ViewOnlyField
          label="Default term"
          value={`${plan.term_months.toString()} months`}
        />
      ) : (
        <Select
          items={TERM_MONTHS_ITEMS}
          label="Default term"
          hint="Contract length that can be overridden by the API"
          {...termMonthsFieldProps}
          onChange={(value: Key | null) => {
            if (value) {
              const item = TERM_MONTHS_ITEMS.find((item) => item.id === value);
              if (item) {
                const termMonthsValue = item.id as TermMonths;
                onTermMonthsChange(termMonthsValue);
                setTermMonths(termMonthsValue);
              }
            }
          }}
          isRequired
        >
          {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      )}
      {isEditingDisabled ? (
        <ViewOnlyField
          label="Monthly base fee"
          value={`$${plan.plan_monthly_cost_dollars ?? "0.00"}`}
        />
      ) : (
        <Input
          label="Monthly base fee"
          hint={
            baseFeeConstraint &&
            `Field between ${formatDollarAmount(parseFloat(baseFeeConstraint.min_value))}-${formatDollarAmount(parseFloat(baseFeeConstraint.max_value))} visible on each customer's bill`
          }
          icon={CurrencyDollar}
          isRequired
          isInvalid={baseFeeFieldState.invalid}
          {...baseFeeField}
        />
      )}
      {isEditingDisabled ? (
        <ViewOnlyField
          label="Default monthly commission"
          value={`$${plan.partner_commission_per_month_dollars ?? "0.00"}`}
        />
      ) : (
        <Input
          label="Default monthly commission"
          hint={
            monthlyCommissionConstraint &&
            `Payout between ${formatDollarAmount(parseFloat(monthlyCommissionConstraint.min_value))}-${formatDollarAmount(parseFloat(monthlyCommissionConstraint.max_value))} to ${configuration.company_name} that can be overridden by the API`
          }
          icon={CurrencyDollar}
          isRequired
          isInvalid={monthlyCommissionFieldState.invalid}
          {...monthlyCommissionField}
        />
      )}
    </div>
  );
};
